
import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { BlueUserStore } from '../store'
import { SessionService } from '@evertel/session/SessionService'
import { Api } from '@evertel/api'
import { DepartmentsAccessStore } from '@evertel/departments-access'
import { getRole } from '@evertel/utils'
import { DepartmentStore } from '@evertel/department'
import { APIDataBlueUser } from '@evertel/types'

class CurrentUserController {

    constructor(
        private api: Api,
        private userStore: BlueUserStore,
        private session: SessionService,
        private departmentsAccessStore: DepartmentsAccessStore,
        private departmentStore: DepartmentStore
    ) {
        makeAutoObservable(this)
    }

    update = async (data: any) => {
        if (!data) return

        try {
            const userData = await this.api.Routes.BlueUser.putById(this.session.currentUserId, data)

            runInAction(() => {
                this.userStore.update(userData)
            })
        } catch (e) {
            console.error({
                type: 'API put',
                message: 'CurrentUserController.update()',
                error: e.message,
                userId: this.session.currentUserId
            })
            return
        }
    }

    deleteProfilePic = async () => {
        if (!this.user) return

        const result = await this.api.Routes.BlueUser.delPublicMedia(this.user.id)

        runInAction(() => {
            this.userStore.update(result)
        })
    }

    updateLocalStore = (data: any) => {
        runInAction(() => {
            this.userStore.update({ ...data, id: this.user.id })
        })
    }

    updateReportingDepartment = async (position: string, department: string) => {
        if (!position || !department) return

        try {
            const userData = await this.api.Routes.BlueUser.putReportingDepartment(
                this.session.currentUserId,
                { reportingDepartment: { position, department }}
            )

            runInAction(() => {
                this.userStore.update(userData)
            })
        } catch (e) {
            console.error({
                type: 'API put',
                message: 'CurrentUserController.updateReportingDepartment()',
                error: e.message,
                userId: this.session.currentUserId
            })
            throw e
        }
    }

    get user(): APIDataBlueUser {
        return this.userStore.findById(this.session.currentUserId)
    }

    get fullName() {
        const name = (this.user?.firstName + ' ' + this.user?.lastName).trim()
        return name
    }

    get departmentsAccess() {
        return this.departmentsAccessStore.find(da => da.blueUserId === this.session.currentUserId)
    }

    get departmentsAccessNotGuest() {
        return this.departmentsAccess?.filter((da: any) => getRole(da.roles) !== 'guest')
    }

    get guestDepartmentsAccess() {
        return this.departmentsAccess?.filter((da: any) => getRole(da.roles) === 'guest')
    }

    get selectedDepartmentsAccess() {
        return this.departmentsAccess?.find((da: any) => da.departmentId === this.session.selectedDepartmentId) || {}
    }

    get primaryDepartmentsAccess() {
        return this.departmentsAccess?.find((da: any) => da.isPrimary === true) || {}
    }

    get canManage() {
        if (!this.selectedDepartmentsAccess) return false

        return this.selectedDepartmentsAccess.roles?.includes('executive')
                || this.selectedDepartmentsAccess.roles?.includes('management')
                || false
    }

    get canExecutive() {
        if (!this.selectedDepartmentsAccess) return false

        return this.selectedDepartmentsAccess.roles?.includes('executive')
    }

    get hasGuestRoleInSelectedDepartment() {
        return getRole(this.selectedDepartmentsAccess.roles) === 'guest'
    }

    get selectedDepartmentRole() {
        if (!this.selectedDepartmentsAccess?.roles) return undefined

        return getRole(this.selectedDepartmentsAccess.roles)
    }

    get primaryDepartmentRole() {
        if (!this.primaryDepartmentsAccess?.roles) return undefined

        return getRole(this.primaryDepartmentsAccess.roles)
    }

    get primaryDepartment() {
        const primaryDeptId = this.primaryDepartmentsAccess?.departmentId
        return this.departmentStore.findById(primaryDeptId)
    }

    get selectedDepartment() {
        const selectedDeptId = this.selectedDepartmentsAccess?.departmentId
        return this.departmentStore.findById(selectedDeptId)
    }

    get isAdmin() {
        return this.user?.isAdmin || false
    }

    get reportingDepartment(): { position: string, department: string } | undefined {
        const reportingDepartment = this.user?.reportingDepartment
        return reportingDepartment?.department ? reportingDepartment : undefined
    }

    getRoleInDepartment(departmentId: number): string | undefined {
        const da = this.departmentsAccess?.find((da: any) => da.departmentId === departmentId)

        // You might not be a member at all in this department
        if (!da) return undefined

        // You are not a member if you are not verified
        if (!da.isVerified) return undefined

        return getRole(da.roles)
    }

    canManageInDepartment(departmentId: number): boolean {
        const role = this.getRoleInDepartment(departmentId)
        return role === 'executive' || role === 'management'
    }

    canExecutiveInDepartment(departmentId: number): boolean {
        const role = this.getRoleInDepartment(departmentId)
        return role === 'executive'
    }

    isGuestInDepartment(departmentId: number): boolean {
        const role = this.getRoleInDepartment(departmentId)
        return role === 'guest'
    }

    /**
     * Returns true if user is a verified non-guest member of the department.
     * Use this instead of !isGuestInDepartment() when checking for "real" membership.
     */
    isMemberInDepartment(departmentId: number): boolean {
        const role = this.getRoleInDepartment(departmentId)
        return role !== undefined && role !== 'guest'
    }
}

decorate(injectable(), CurrentUserController)
decorate(inject(Api), CurrentUserController, 0)
decorate(inject(BlueUserStore), CurrentUserController, 1)
decorate(inject(SessionService), CurrentUserController, 2)
decorate(inject(DepartmentsAccessStore), CurrentUserController, 3)
decorate(inject(DepartmentStore), CurrentUserController, 4)

export { CurrentUserController }
